/* ================================================
   DGN ZAUN THEME - GLOBAL JAVASCRIPT
   ================================================ */

class DGNTheme {
  constructor() {
    this.init();
  }

  init() {
    this.initMobileMenu();
    this.initSearch();
    this.initStickyHeader();
    this.initAccordions();
    this.initQuantitySelectors();
    // Cart system is now handled by cart-system.js
  }

  // Mobile Menu
  initMobileMenu() {
    const menuToggle = document.querySelector('[data-mobile-menu-toggle]');
    const mobileMenu = document.querySelector('[data-mobile-menu]');
    const mobileMenuClose = document.querySelector('[data-mobile-menu-close]');
    const mobileMenuOverlay = document.querySelector('[data-mobile-menu-overlay]');
    const mobileMenuToggles = document.querySelectorAll('[data-mobile-submenu-toggle]');

    if (!menuToggle || !mobileMenu) return;

    const openMenu = () => {
      mobileMenu.classList.add('is-active');
      mobileMenuOverlay?.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    };

    const closeMenu = () => {
      mobileMenu.classList.remove('is-active');
      mobileMenuOverlay?.classList.remove('is-active');
      document.body.style.overflow = '';
    };

    menuToggle.addEventListener('click', openMenu);
    mobileMenuClose?.addEventListener('click', closeMenu);
    mobileMenuOverlay?.addEventListener('click', closeMenu);

    // Submenu toggles
    mobileMenuToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        const parent = toggle.closest('.mobile-menu__item');
        parent?.classList.toggle('is-open');
      });
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && mobileMenu.classList.contains('is-active')) {
        closeMenu();
      }
    });
  }

  // Search Toggle
  initSearch() {
    const searchToggle = document.querySelector('[data-search-toggle]');
    const searchContainer = document.querySelector('[data-header-search]');
    const searchClose = document.querySelector('[data-search-close]');
    const searchInput = document.querySelector('[data-search-input]');

    if (!searchToggle || !searchContainer) return;

    const openSearch = () => {
      searchContainer.classList.add('is-active');
      searchInput?.focus();
    };

    const closeSearch = () => {
      searchContainer.classList.remove('is-active');
    };

    searchToggle.addEventListener('click', openSearch);
    searchClose?.addEventListener('click', closeSearch);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && searchContainer.classList.contains('is-active')) {
        closeSearch();
      }
    });
  }

  // Sticky Header
  initStickyHeader() {
    const header = document.querySelector('[data-header]');
    if (!header) return;

    const isSticky = header.dataset.sticky === 'true';
    if (!isSticky) return;

    let lastScroll = 0;
    const headerHeight = header.offsetHeight;

    const handleScroll = () => {
      const currentScroll = window.pageYOffset;

      if (currentScroll > headerHeight) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }

      // Optional: Hide header on scroll down, show on scroll up
      // if (currentScroll > lastScroll && currentScroll > headerHeight * 2) {
      //   header.style.transform = 'translateY(-100%)';
      // } else {
      //   header.style.transform = 'translateY(0)';
      // }

      lastScroll = currentScroll;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
  }

  // Cart functions removed - now handled by cart-system.js

  // Accordions
  initAccordions() {
    const accordionToggles = document.querySelectorAll('[data-accordion-toggle]');

    accordionToggles.forEach(toggle => {
      toggle.addEventListener('click', () => {
        const parent = toggle.closest('[data-accordion-item]');
        const content = parent?.querySelector('[data-accordion-content]');

        if (!parent || !content) return;

        const isOpen = parent.classList.contains('is-open');

        // Close other accordions in the same group (optional)
        const group = parent.closest('[data-accordion-group]');
        if (group) {
          group.querySelectorAll('[data-accordion-item].is-open').forEach(item => {
            if (item !== parent) {
              item.classList.remove('is-open');
              const itemContent = item.querySelector('[data-accordion-content]');
              if (itemContent) itemContent.style.maxHeight = null;
            }
          });
        }

        // Toggle current accordion
        parent.classList.toggle('is-open');

        if (!isOpen) {
          content.style.maxHeight = content.scrollHeight + 'px';
        } else {
          content.style.maxHeight = null;
        }
      });
    });
  }

  // Quantity Selectors
  initQuantitySelectors() {
    const quantityInputs = document.querySelectorAll('[data-quantity-input]');

    quantityInputs.forEach(input => {
      const parent = input.closest('[data-quantity-selector]');
      const decreaseBtn = parent?.querySelector('[data-quantity-decrease]');
      const increaseBtn = parent?.querySelector('[data-quantity-increase]');

      if (!decreaseBtn || !increaseBtn) return;

      decreaseBtn.addEventListener('click', () => {
        const currentValue = parseInt(input.value) || 1;
        const min = parseInt(input.min) || 1;
        if (currentValue > min) {
          input.value = currentValue - 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      increaseBtn.addEventListener('click', () => {
        const currentValue = parseInt(input.value) || 1;
        const max = parseInt(input.max) || 999;
        if (currentValue < max) {
          input.value = currentValue + 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }
}

// Cart API Helper Functions
const CartAPI = {
  async getCart() {
    const response = await fetch('/cart.js');
    return response.json();
  },

  async addItem(id, quantity = 1, properties = {}) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id,
        quantity,
        properties,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to add item to cart');
    }

    const cart = await response.json();
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    return cart;
  },

  async updateItem(line, quantity) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        line,
        quantity,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to update cart');
    }

    const cart = await response.json();
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    return cart;
  },

  async removeItem(line) {
    return this.updateItem(line, 0);
  },
};

// Utility Functions
const Utils = {
  formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || window.theme?.moneyFormat || '€{{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || '.';
      decimal = decimal || ',';

      if (isNaN(number) || number === null) {
        return '0';
      }

      number = (number / 100.0).toFixed(precision);

      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';

      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'");
        break;
    }

    return formatString.replace(placeholderRegex, value);
  },

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  },

  throttle(func, limit) {
    let inThrottle;
    return function executedFunction(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  },
};

// Initialize theme
document.addEventListener('DOMContentLoaded', () => {
  window.DGNTheme = new DGNTheme();
  window.CartAPI = CartAPI;
  window.Utils = Utils;
});
